import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { enqueueSnackbar } from "notistack";
import { useSelector } from "react-redux";
import getVoidTicketRequest from "../../../../controllers/booking/postTicketing/void/getVoidTicketRequest";
import addVoidMessage from "../../../../controllers/booking/postTicketing/void/addVoidMessage";
import { normalizeVoidOrder } from "./VoidReviewCard";
import {
  hydratedThread,
  inOrder,
  mergeThread,
  savedOrEcho,
} from "../../../../utils/order/conversationThread";

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function senderLabel(account, adminId) {
  if (account && adminId && String(account) === String(adminId)) return "You";
  return "Agent";
}

export default function VoidMessagesModal({ open, setOpen, data, onThreadChange }) {
  const { user: adminUser } = useSelector((state) => state.user?.userData) || {};
  const adminId = adminUser?._id;
  const voidId =
    typeof data === "object" ? data?._id || data?.id : data;
  const [loadedVoid, setLoadedVoid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState("");
  const [thread, setThread] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const bottomRef = useRef(null);
  const loadGen = useRef(0);

  async function loadMessages({ soft = false } = {}) {
    if (!voidId) return;
    const gen = ++loadGen.current;
    if (soft) setRefreshing(true);
    else {
      setLoadedVoid(null);
      setLoading(true);
    }
    try {
      const res = await getVoidTicketRequest(voidId);
      if (gen !== loadGen.current) return;
      if (res?.return) {
        setLoadedVoid(res.data);
        const next = inOrder(hydratedThread(res.data?.conversation));
        setThread(next);
        onThreadChange?.(next);
      } else {
        enqueueSnackbar(res?.msg || "Could not load messages.", {
          variant: "error",
        });
      }
    } finally {
      if (gen === loadGen.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }

  useEffect(() => {
    if (!open || !voidId) return;
    loadMessages();
    return () => {
      loadGen.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when panel opens / id changes
  }, [open, voidId]);

  const headerOrder = useMemo(() => {
    const source =
      typeof data === "object" && data?.orderId ? data : loadedVoid || data;
    const order = normalizeVoidOrder(source) || {};
    const orderId = order.orderId;
    const pnr = order.flightBooking?.pnr;
    if (!orderId && !pnr) return null;
    return { orderId, pnr };
  }, [data, loadedVoid]);

  useEffect(() => {
    if (open && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [open, thread.length]);

  function close() {
    if (sending) return;
    setOpen?.(false);
    setDraft("");
  }

  async function handleSend() {
    const text = draft.trim();
    if (!text || !voidId) return;

    setSending(true);
    const res = await addVoidMessage(voidId, { message: text }).finally(() =>
      setSending(false),
    );

    if (res?.return) {
      const echo = savedOrEcho(res.data, text, adminId);
      const next = mergeThread(thread, [echo]);
      setThread(next);
      onThreadChange?.(next);
      setDraft("");
    } else {
      enqueueSnackbar(res?.msg || "Could not send message.", {
        variant: "error",
      });
    }
  }

  function onKeyDown(ev) {
    if (ev.key === "Enter" && !ev.shiftKey) {
      ev.preventDefault();
      handleSend();
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/40"
        onClick={close}
      />
      <div className="relative flex flex-col w-full max-w-[520px] max-h-[85vh] rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-2 border-b border-gray-100">
          <div>
            <h2 className="text-[17px] font-semibold text-gray-900">
              Messages with agent
            </h2>
            {headerOrder ? (
              <p className="text-sm font-medium text-gray-800 mt-1">
                {headerOrder.orderId ? (
                  <span>Order {headerOrder.orderId}</span>
                ) : null}
                {headerOrder.orderId && headerOrder.pnr ? (
                  <span className="text-gray-400 font-normal"> · </span>
                ) : null}
                {headerOrder.pnr ? (
                  <span className="text-primary1">PNR {headerOrder.pnr}</span>
                ) : null}
              </p>
            ) : null}
            <p className="text-xs text-gray-500 mt-0.5">
              Text only — no attachments. Same thread as the agent tracker. We
              do not push notifications — check here often.
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              title="Refresh messages"
              aria-label="Refresh messages"
              disabled={loading || refreshing || sending}
              onClick={() => loadMessages({ soft: true })}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
            >
              <Icon
                icon="material-symbols:refresh"
                className={`text-xl ${refreshing ? "animate-spin" : ""}`}
              />
            </button>
            <button
              type="button"
              onClick={close}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-100"
            >
              <Icon icon="material-symbols:close" className="text-xl" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3 min-h-[200px] max-h-[50vh] bg-[#F8FAFC]">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="primaryLoader" />
            </div>
          ) : thread.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              No messages yet. Ask the agent for anything you need before you
              void — reply here when you have a question.
            </p>
          ) : (
            <ul className="space-y-3">
              {thread.map((msg, i) => (
                <li
                  key={msg._id || `msg-${i}`}
                  className={`rounded-xl px-3 py-2 text-sm ${
                    String(msg.account) === String(adminId)
                      ? "bg-primary1/10 ml-6"
                      : "bg-white border border-[#E8EEF5] mr-6"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className="text-xs font-semibold text-gray-800">
                      {senderLabel(msg.account, adminId)}
                    </span>
                    {msg.createdAt ? (
                      <span className="text-[10px] text-gray-400">
                        {formatWhen(msg.createdAt)}
                      </span>
                    ) : null}
                  </div>
                  {msg.message ? (
                    <p className="text-gray-800 whitespace-pre-wrap break-words">
                      {msg.message}
                    </p>
                  ) : null}
                </li>
              ))}
              <li ref={bottomRef} />
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 bg-white">
          <textarea
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={sending}
            placeholder="Write to the agent… Enter to send, Shift+Enter for new line"
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-primary1 resize-none"
          />
          <div className="flex items-center justify-end mt-2 gap-2">
            <button
              type="button"
              disabled={sending || !draft.trim()}
              onClick={handleSend}
              className="h-9 px-4 rounded-xl bg-primary1 text-white text-sm font-semibold disabled:opacity-50"
            >
              {sending ? <span className="buttonLoader" /> : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
